import { isFetchFailure } from '@/lib/telemetry/noteGenerateTracking';
import { NoteGenerationErrorCode } from '@/models/analytics/contracts/note-failed-error-code';
import { logger } from '@/utils/logger/logger';
import {
  finishTracking,
  type GenerateTrigger,
  recordSessionFlowIntent,
  type SessionFlowFailure,
  startTracking,
  type TrackingHandle,
  TrackingKey,
} from '@heidi/runtime/performance';
import { type DocumentGenType } from '@heidi/schema/ml-scribe';
import { TRANSCRIPT_DELETED_ERROR } from '@heidi/session/contracts';

// `document_generate` metrics: one attempt per `generateDocument` run, closed
// once. Only a user action emits intent.

/** Who asked for the generation. Only `explicit` emits intent. */
export type DocumentGenerateTrigger = GenerateTrigger;

/** Why a run was cancelled. */
export type DocumentGenerateCancelReason =
  | 'user'
  | 'superseded'
  | 'cleanup'
  | 'unauthorized'
  | 'unknown';

/** `unauthorized` comes only from a 401, never from an abort. */
type AbortCancelReason = Exclude<DocumentGenerateCancelReason, 'unauthorized'>;

export type DocumentGenerateErrorName =
  | 'DocumentGenerateQuotaExceeded'
  | 'DocumentGeneratePermissionDenied'
  | 'DocumentGenerateTemplateUnavailable'
  | 'DocumentGenerateTranscriptDeleted'
  | 'DocumentGenerateSessionNotTranscribed'
  | 'DocumentGenerateServerError'
  | 'DocumentGenerateRateLimited'
  | 'DocumentGenerateRejected'
  | 'DocumentGenerateTimeout'
  | 'DocumentGenerateNetworkUnavailable'
  | 'DocumentGenerateInterrupted'
  | 'DocumentGenerateCancelled'
  | 'DocumentGenerateUnclassified';

/** Failure names. Unclassified is left out, so it reports `_OTHER`. */
export const DOCUMENT_GENERATE_FAILURE_TYPES: Readonly<
  Record<
    Exclude<DocumentGenerateErrorName, 'DocumentGenerateUnclassified'>,
    SessionFlowFailure
  >
> = {
  DocumentGenerateQuotaExceeded: { outcome: 'blocked', errorType: 'quota_exceeded' },
  DocumentGeneratePermissionDenied: {
    outcome: 'blocked',
    errorType: 'permission_denied',
  },
  DocumentGenerateTemplateUnavailable: {
    outcome: 'failed',
    errorType: 'template_unavailable',
  },
  // Missing transcript is a precondition, not a failure.
  DocumentGenerateTranscriptDeleted: {
    outcome: 'blocked',
    errorType: 'transcript_deleted',
  },
  DocumentGenerateSessionNotTranscribed: {
    outcome: 'blocked',
    errorType: 'session_not_transcribed',
  },
  DocumentGenerateServerError: { outcome: 'failed', errorType: 'server_error' },
  DocumentGenerateRateLimited: { outcome: 'failed', errorType: 'rate_limited' },
  DocumentGenerateRejected: { outcome: 'failed', errorType: 'request_rejected' },
  DocumentGenerateTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  DocumentGenerateNetworkUnavailable: {
    outcome: 'failed',
    errorType: 'network_unavailable',
  },
  // 401: the session ended, not the generation.
  DocumentGenerateInterrupted: { outcome: 'cancelled' },
  DocumentGenerateCancelled: { outcome: 'cancelled' },
};

const namedError = (name: DocumentGenerateErrorName): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

// A non-2xx stream rejects with the backend `detail.type` as its message.
const NAME_BY_MESSAGE: Readonly<Record<string, DocumentGenerateErrorName>> = {
  [NoteGenerationErrorCode.UnlimitedQuotaUntilNotSet]: 'DocumentGenerateQuotaExceeded',
  [NoteGenerationErrorCode.AskAiQuotaExceed]: 'DocumentGenerateQuotaExceeded',
  [NoteGenerationErrorCode.TemplateQuotaExceed]: 'DocumentGenerateQuotaExceeded',
  [NoteGenerationErrorCode.TeamTemplateAccessDenied]: 'DocumentGeneratePermissionDenied',
  [NoteGenerationErrorCode.GenerationTemplateAccessDenied]:
    'DocumentGeneratePermissionDenied',
  TemplateAccessDeniedError: 'DocumentGeneratePermissionDenied',
  [NoteGenerationErrorCode.TemplateDeleted]: 'DocumentGenerateTemplateUnavailable',
  [NoteGenerationErrorCode.TemplateNotFound]: 'DocumentGenerateTemplateUnavailable',
  [TRANSCRIPT_DELETED_ERROR]: 'DocumentGenerateTranscriptDeleted',
  SessionNotTranscribedError: 'DocumentGenerateSessionNotTranscribed',
};

// A 403 without a known `detail.type` (WAF, edge, auth) is a failure, not
// `permission_denied`, so it stays visible in the failure rate.
const nameForHttpStatus = (status: number): DocumentGenerateErrorName => {
  if (status === 401) return 'DocumentGenerateInterrupted';
  if (status === 408 || status === 504) return 'DocumentGenerateTimeout';
  if (status === 429) return 'DocumentGenerateRateLimited';
  if (status >= 500) return 'DocumentGenerateServerError';
  if (status >= 400) return 'DocumentGenerateRejected';
  return 'DocumentGenerateUnclassified';
};

/** Maps a rejection to a fixed name, in a new error so no raw text leaks. */
export const classifyDocumentGenerateError = (error: unknown): Error => {
  if (isFetchFailure(error)) return namedError('DocumentGenerateNetworkUnavailable');
  if (!(error instanceof Error)) return namedError('DocumentGenerateUnclassified');

  // Own keys only: `toString` and friends must not match.
  const known = Object.hasOwn(NAME_BY_MESSAGE, error.message)
    ? NAME_BY_MESSAGE[error.message]
    : undefined;
  if (known) return namedError(known);

  // Stream errors carry `status`; the axios save carries `response.status`.
  const { code, status, response } = error as {
    code?: unknown;
    status?: unknown;
    response?: { status?: unknown };
  };
  const httpStatus = typeof status === 'number' ? status : response?.status;
  if (typeof httpStatus === 'number') {
    return namedError(nameForHttpStatus(httpStatus));
  }
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return namedError('DocumentGenerateTimeout');
  }
  if (code === 'ERR_NETWORK') return namedError('DocumentGenerateNetworkUnavailable');
  return namedError('DocumentGenerateUnclassified');
};

/**
 * `user`, `superseded` and `cleanup` pass through. A non-string reason (Stop
 * aborts with none) is `user`; any other string is `unknown`.
 */
export const documentGenerateCancelReason = (reason: unknown): AbortCancelReason => {
  if (reason === 'user' || reason === 'superseded' || reason === 'cleanup') {
    return reason;
  }
  return typeof reason === 'string' ? 'unknown' : 'user';
};

// Telemetry must never break the generation.
const safely = (step: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    logger.warn('[documentGenerateTracking] telemetry step failed', {
      step,
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
};

export type DocumentGenerateAttempt = {
  complete: () => void;
  /** Sends only the classified name, never the raw error. */
  fail: (error: unknown) => void;
  /** A 2xx stream closed with no content; other statuses reject instead. */
  failEmptyStream: () => void;
  cancel: (reason: AbortCancelReason) => void;
};

/** Opens one attempt (intent first if explicit). The first close wins. */
export const beginDocumentGenerateAttempt = ({
  trigger,
  generationMethod,
}: {
  trigger: DocumentGenerateTrigger;
  generationMethod?: DocumentGenType | null;
}): DocumentGenerateAttempt => {
  let handle: TrackingHandle | undefined;
  if (trigger === 'explicit') {
    safely('intent', () =>
      recordSessionFlowIntent(TrackingKey.SCRIBE_DOCUMENT_GENERATION)
    );
  }
  safely('begin', () => {
    handle = startTracking({
      key: TrackingKey.SCRIBE_DOCUMENT_GENERATION,
      instanced: true,
      metadata: {
        trigger,
        ...(generationMethod ? { generation_method: generationMethod } : {}),
      },
    });
  });

  const close = (step: string, finish: (handle: TrackingHandle) => void) =>
    safely(step, () => {
      if (!handle) return;
      const open = handle;
      handle = undefined;
      finish(open);
    });

  const failWith = (step: string, error: Error, metadata: Record<string, unknown>) =>
    close(step, (open) =>
      finishTracking({ handle: open, outcome: 'failed', error, metadata })
    );

  const failClassified = (
    step: string,
    error: Error,
    metadata: Record<string, unknown>
  ) =>
    failWith(step, error, {
      ...metadata,
      ...(error.name === 'DocumentGenerateInterrupted'
        ? { cancel_reason: 'unauthorized' satisfies DocumentGenerateCancelReason }
        : {}),
    });

  return {
    complete: () =>
      close('complete', (open) =>
        finishTracking({ handle: open, metadata: { result: 'success' } })
      ),
    fail: (error) =>
      failClassified('fail', classifyDocumentGenerateError(error), { result: 'error' }),
    failEmptyStream: () =>
      failWith('empty_stream', namedError('DocumentGenerateServerError'), {
        result: 'error',
        empty_stream: true,
      }),
    cancel: (reason) =>
      failWith('cancel', namedError('DocumentGenerateCancelled'), {
        cancel_reason: reason,
      }),
  };
};
